import { FC, useState, useMemo, useEffect } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useQuery, useMutation } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { observer } from 'mobx-react-lite';
import { GridColumns, DataGrid, GridCellParams } from '@material-ui/data-grid';
import IconButton from '@material-ui/core/IconButton';
import LayersIcon from '@material-ui/icons/Layers';
import { useStyles } from './styles';
import { Paper, Tooltip, Snackbar, SnackbarContent } from '@material-ui/core';
import EmailIcon from '../../../assets/icons/EndUser/Email.svg';
import SyncIcon from '@mui/icons-material/Sync';
import SyncDisabledIcon from '@material-ui/icons/SyncDisabled';
import { green, red } from '@material-ui/core/colors';
import { getRowsPerPageOptions } from 'utils/pagination';

export const SyncGroup: FC = observer(() => {
  const classes = useStyles();
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const {
    googleStore: { syncGroup, allsyncGroupsView, reSyncSingleGroup, reSyncGroup },
    ldapStore: { getGroups, groupsView },
    notificationStore: { openNotification },
  } = useStore();
  const [syncedGroups, setSyncedGroups] = useState(new Set());
  const syncGroupReq = useQuery('syncGroupReq', () => syncGroup(), {
    onError: () => openNotification('error', 'We are unable to get the group from Google'),
  });
  const getGroupsReq = useQuery('getGroups', getGroups, {
    onError: () => openNotification('error', `Can't get group`),
  });
  const reSyncGroupReq = useMutation(reSyncGroup, {
    mutationKey: 'reSyncGroup',
    onSuccess: () => {
      setOpen(false);
      setSyncedGroups(new Set(allsyncGroupsView.map((group) => group.mail)));
      openNotification('success', 'Bi-direction Sync Successful');
      syncGroupReq.refetch();
      getGroupsReq.refetch();
    },
    onError: ({ message }: Error) => {
      setOpen(false);
      openNotification('error', message);
    },
  });

  const reSyncSingleGroupReq = useMutation((data: { email: string }) => reSyncSingleGroup(data), {
    mutationKey: 'reSyncSingleGroupReq',
    onSuccess: (data, variables) => {
      openNotification('success', 'Selected Groups sync’ed with App Google Successfully');
      setSyncedGroups((prev) => new Set(prev).add(variables.email));
      syncGroupReq.refetch();
      getGroupsReq.refetch();
    },
    onError: (error: any) => {
      setOpen(false);
      openNotification('error', `We are unable to sync the group from google`);
    },
  });

  const googleGroupCount = allsyncGroupsView.length ?? 0;
  const ldapGroupCount = groupsView.length ?? 0;
  // const difference = Math.abs(ldapGroupCount - googleGroupCount);
  useEffect(() => {
    document.title = 'EasyIdentity: App: SyncGroup';
  }, []);

  const handleSync = (email: string) => {
    reSyncSingleGroupReq.mutate({ email });
  };
  const handleSyncAll = () => {
    setLoadingMessage('Sync is in Progress. Please be patient, it may take few minutes.');
    reSyncGroupReq.mutate();
    setOpen(true);
  };
  useAppPreloader(
    syncGroupReq.isFetching || reSyncGroupReq.isLoading || getGroupsReq.isFetching || reSyncSingleGroupReq.isLoading
  );

  const rows = useMemo(() => {
    return !syncGroupReq.isFetching ? allsyncGroupsView : [];
  }, [syncGroupReq.isFetching, allsyncGroupsView]);

  const filteredGroupSet = useMemo(() => {
    return new Set(groupsView.map((item) => item.google_id));
  }, [groupsView]);

  const [pageSize, setPageSize] = useState(10);
  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(rows), [rows]);

  const columns: GridColumns = [
    {
      field: 'icon',
      headerName: ' ',
      width: 40,
      align: 'center',
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => <LayersIcon style={{ color: 'grey' }} />,
    },
    {
      field: 'cn',
      headerName: 'NAME',
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={params.row.cn}>
          <div>
            <span>{params.row.cn}</span>
          </div>
        </Tooltip>
      ),
    },
    {
      field: 'mail',
      headerName: 'EMAIL',
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const value = String(params.value ?? '');
        return (
          <Tooltip title={value} placement='bottom'>
            <div style={{ display: 'flex', gap: '10px' }}>
              <img src={EmailIcon} alt='' />
              <span>{value}</span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'description',
      headerName: 'DESCRIPTION',
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={params.row.description}>
          <div>
            <span>{params.row.description}</span>
          </div>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 200,
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => (
        <>
          {!filteredGroupSet.has(params.row.gidNumber) && !syncedGroups.has(params.row.mail) ? (
            <Tooltip title='Not Synced'>
              <IconButton size='small' onClick={() => handleSync(params.row.mail)}>
                <SyncDisabledIcon style={{ color: red[500] }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title='Sync'>
              <IconButton size='small' onClick={() => handleSync(params.row.mail)}>
                <SyncIcon style={{ color: green[500] }} />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      {!syncGroupReq.isFetching && !getGroupsReq.isFetching && (
        <Paper className={classes.dataGrid} elevation={0}>
          <>
            <h1 className={classes.textBox}>You have {ldapGroupCount} Groups in EasyIdentity </h1>
            <h1 className={classes.textBox}>You have {googleGroupCount} Groups in Google Workspace</h1>
            <div style={{ display: 'flex' }}>
              <h1 className={classes.textBox}>
                Sync Groups : EasyIdentity {'<->'} Google Workspace
                <SyncIcon style={{ color: green[500], cursor: 'pointer' }} onClick={handleSyncAll} />
              </h1>
            </div>
          </>
        </Paper>
      )}
      {/* {!syncGroupReq.isFetching && !getGroupsReq.isFetching && difference === 0 && (
        <Paper className={classes.dataGrid1} elevation={0}>
          <h1 style={{ fontWeight: '400', fontSize: '120%' }}>
            Congrats.Here is the list of Groups that have been Synced with the Google Workspace.
          </h1>
        </Paper>
      )} */}
      <Paper className={classes.dataGrid} elevation={0}>
        <>
          <h1 style={{ fontWeight: '400', fontSize: '120%', textAlign: 'center' }}>
            {' '}
            List of All Google Workspace Groups
          </h1>
        </>
      </Paper>
      <Paper className={classes.dataGrid} elevation={0}>
        <DataGrid
          className={classes.noBorder}
          autoHeight
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          rowsPerPageOptions={rowsPerPageOptions}
          pagination
          onPageChange={(params) => setPageSize(params.pageSize)}
        />
      </Paper>
      <Snackbar open={open}>
        <SnackbarContent style={{ backgroundColor: green[500] }} message={loadingMessage} />
      </Snackbar>
    </>
  );
});
